/**
 * bracketFieldUtils.js — Pure helper functions for bracket/bold field extraction, 
 * input type detection, and placeholder text. Extracted from App.js.
 */

// Extract unfilled bracket [fields] and **bold fields** from text
export const extractBracketFields = (text, formState = {}) => {
  const { soldierRank, soldierFirstName, soldierLastName, soldierUnit, soldierDob, soldierPhone,
    providerSignatureBlock, mtfName, customMtf, occupation, jobDuties } = formState;
  const bracketRegex = /\[([^\]]+)\]/g;
  const boldRegex = /\*\*([^*]+)\*\*/g;
  const fields = new Set();
  let match;

  while ((match = bracketRegex.exec(text)) !== null) {
    const field = match[1], fl = field.toLowerCase();
    if (fl.includes('rank') && soldierRank) continue;
    if ((fl.includes('first name') || fl === 'first') && soldierFirstName) continue;
    if ((fl.includes('last name') || fl === 'last') && soldierLastName) continue;
    if (fl.includes('unit') && soldierUnit) continue;
    if ((fl.includes('provider') || fl.includes('signature')) && providerSignatureBlock) continue;
    if ((fl.includes('mtf') || fl.includes('clinic')) && (mtfName || customMtf)) continue;
    if (fl.includes('dob') && soldierDob) continue;
    if ((fl.includes('date of birth') || fl.includes('birth date')) && soldierDob) continue;
    if (fl.includes('phone') && soldierPhone) continue;
    if ((fl.includes('patient name') || fl.includes('full name') || fl === 'name') && soldierFirstName && soldierLastName) continue;
    if ((fl.includes('occupation') || fl.includes('job title') || fl.includes('mos') || fl.includes('aoc')) && occupation) continue;
    if (fl.includes('job dut') && jobDuties) continue;
    fields.add(field);
  }

  while ((match = boldRegex.exec(text)) !== null) {
    const field = match[1], fl = field.toLowerCase();
    if (['functional limitations', 'duty restrictions', 'accommodations', 'symptoms', 'duration', 'reassessment'].some(s => fl.includes(s))) continue;
    if (field.includes(':')) continue;
    if (fl.includes('rank') && soldierRank) continue;
    if ((fl.includes('first name') || fl === 'first') && soldierFirstName) continue;
    if ((fl.includes('last name') || fl === 'last') && soldierLastName) continue;
    if (fl.includes('name') && (soldierFirstName && soldierLastName)) continue;
    if ((fl.includes('occupation') || fl.includes('job title') || fl.includes('mos')) && occupation) continue;
    if (fl.includes('job dut') && jobDuties) continue;
    fields.add(field);
  }
  return Array.from(fields);
};

// Determine input type for a bracket field
export const getFieldInputType = (field) => {
  const fl = field.toLowerCase();
  if (fl.includes('weight') || fl.includes('kg') || fl.includes('lbs') || fl.includes('pounds')) return 'number';
  if (fl.includes('age') || fl.includes('count') || fl.includes('percentage') || fl === 'x') return 'number';
  if (fl.includes('date') || fl.includes('when')) return 'date';
  if ((fl.includes('start') || fl.includes('end')) && fl.includes('date')) return 'date';
  if (fl.includes('duration') || fl.includes('length') || fl.includes('how long') || fl.includes('time frame') || fl.includes('period')) return 'duration';
  if (fl.includes('phone') || fl.includes('number')) return 'tel';
  return 'text';
};

// Get placeholder text for a bracket field
export const getFieldPlaceholder = (field) => {
  const fl = field.toLowerCase();
  if (fl.includes('weight')) return 'e.g., 185 lbs, 84 kg';
  if (fl.includes('date')) return 'e.g., January 15, 2025';
  if (fl.includes('duration') || fl.includes('length')) return 'e.g., 6 months, 2 years';
  if (fl.includes('injury')) return 'e.g., low back pain, knee injury';
  if (fl.includes('condition')) return 'e.g., chronic lower back pain';
  if (fl.includes('limitation') || fl.includes('restriction')) return 'e.g., no lifting over 20 lbs';
  if (fl.includes('frequency')) return 'e.g., 2-3 times per week';
  if (fl.includes('medication')) return 'e.g., Ibuprofen 800mg';
  return `Enter ${field.toLowerCase()}...`;
};

// Apply filled field values to text (replace both [field] and **field**)
export const applyFieldValues = (text, fieldValues) => {
  let result = text;
  Object.entries(fieldValues).forEach(([field, value]) => {
    if (value) {
      const escaped = field.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      result = result.replace(new RegExp(`\\[${escaped}\\]`, 'gi'), value);
      result = result.replace(new RegExp(`\\*\\*${escaped}\\*\\*`, 'gi'), value);
    }
  });
  return result;
};
